import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, RotateCcw } from 'lucide-react';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import GenericSelect from '../../components/ui/GenericSelect';
import PmAnalyticsFilterBar from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod } from '../../components/performance/pmAnalyticsFilters';
import {
  EvalKpi, FindingsPanel, ScreenField, ScreenFilters,
} from '../../components/performance/evaluation/EvaluationParts';
import {
  formatNum, listOf, metaOfContext, metricByCode, useScreenParams,
} from '../../components/performance/evaluation/evaluationAnalytics';
import { useAuth } from '../../context/AuthContext';
import { Grid, PageEmpty, PageWrap } from '../performance-operations/styles';
import {
  anyStale, fetchPeriods, oldestCalculatedAt, usePanel,
} from '../performance-operations/config';
import CompetencyHeatmap from './components/CompetencyHeatmap';
import TopAreasPanel from './components/TopAreasPanel';
import CompetencyRecords from './components/CompetencyRecords';
import {
  COMPETENCY_TYPES, GAP_BANDS, LEVELS, NEGATIVE_BANDS, NS, Q, SCREEN_KEYS, dimensionOf, fetchFindings, fetchSummary, screenFilters, viewOf,
} from './config';

// PM Analitik P4 — E05 Yetkinlik ve Gelişim Açıkları (/performance/competency-gaps). İK ve yönetici (kendi kapsamı; sunucu kapısı
// PmAnalyticsScopeRules — PM.Read tek başına yetki değil). Ortak süzgeç şeridi + ekran süzgeçleri (yetkinlik, aile, tür, beklenen
// seviye, fark bandı, gelişim planı [İK + TD]) URL'dedir; her panel kendi isteğini atar (panel başına hata/Tekrar Dene).
// Isı haritası/çubuk/bulgu tıklaması mevcut süzgeçlere koşul ekler ve listeye iner; satır → E14 yetkinlik sekmesi.
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: -4px 0 16px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const staticLookup = (options) => ({
  onSearch: async (q) => options.filter((o) => !q || o.name.toLocaleLowerCase().includes(String(q).toLocaleLowerCase())),
  onGetById: async (id) => options.find((o) => o.id === id) || { id, name: '' },
});

export default function PerformanceCompetencyGapsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { isHr, canWrite } = useAuth();
  const { filters, setFilters, reset } = usePmAnalyticsFilters();
  const [values, setScreen] = useScreenParams(SCREEN_KEYS);
  const screen = useMemo(() => screenFilters(values), [values]);
  const view = viewOf(values);
  const dimension = dimensionOf(values, isHr);
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [metas, setMetas] = useState({});

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);

  // Dönem zorunlu ve temizlenemez: URL'de yoksa (ya da artık yoksa) en yeni dönem seçilir.
  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  const filterKey = JSON.stringify([filters, screen]);
  useEffect(() => { setMetas({}); }, [filterKey]);
  const onMeta = useCallback((key, meta) => setMetas((m) => ({ ...m, [key]: meta })), []);
  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; dönem etkisi en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);

  const summary = usePanel(() => fetchSummary(filters, screen), [filterKey], ready);
  const findings = usePanel(() => fetchFindings(filters, screen), [filterKey], ready);
  useEffect(() => { if (summary.data) onMeta('summary', metaOfContext(summary.data)); }, [summary.data, onMeta]);

  const context = summary.data?.context;
  const tdVisible = !!context?.tdVisible;
  const competencies = useMemo(() => summary.data?.competencies || [], [summary.data]);
  const competencyOptions = useMemo(() => competencies.map((c) => ({ id: c.id, name: c.name })), [competencies]);
  const groupOptions = useMemo(() => [...new Set(competencies.map((c) => c.groupName).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, lang)).map((g) => ({ id: g, name: g })), [competencies, lang]);
  const planOptions = [{ id: 'true', name: t(`${NS}.plan.has`) }, { id: 'false', name: t(`${NS}.plan.hasNot`) }];
  const competencyLookup = useMemo(() => staticLookup(competencyOptions), [competencyOptions]);
  const groupLookup = useMemo(() => staticLookup(groupOptions), [groupOptions]);
  const typeLookup = useMemo(() => staticLookup(COMPETENCY_TYPES.map((x) => ({ id: x, name: t(`${NS}.competencyType.${x}`) }))), [t]);
  const levelLookup = useMemo(() => staticLookup(LEVELS.map((x) => ({ id: x, name: x }))), []);
  const bandLookup = useMemo(() => staticLookup(GAP_BANDS.map((x) => ({ id: x, name: t(`${NS}.gapBand.${x}`) }))), [t]);

  const scrollToList = () => {
    window.setTimeout(() => document.querySelector('[data-panel="records"]')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  };

  // Isı haritası hücresi: aynı yetkinlik + aynı birim (ortak süzgeçle) → açıklar listesi.
  const openCell = ({ competencyId, dimension: dim, unitKey }) => {
    let next = null;
    if (unitKey) {
      if (dim === 'department') next = { ...filters, departmentIds: [unitKey] };
      else if (dim === 'title') next = { ...filters, titleIds: [unitKey] };
      else if (dim === 'grade') next = { ...filters, gradeIds: [unitKey] };
      else if (dim === 'jobFamily') next = { ...filters, jobFamilyIds: [unitKey] };
      else next = { ...filters, managerEmployeeId: unitKey, includeDescendants: false };
    }
    // Birim (ortak süzgeç) ve yetkinlik (ekran süzgeci) TEK URL güncellemesinde.
    setScreen({ [Q.competencies]: [competencyId], [Q.view]: null, [Q.persistent]: null }, next);
    scrollToList();
  };

  const openArea = (competencyId) => {
    setScreen({ [Q.competencies]: [competencyId], [Q.bands]: NEGATIVE_BANDS, [Q.view]: null });
    scrollToList();
  };

  // Bulgu aksiyonu: önerilen süzgeci listeye uygular (IDP daveti listede satır/sayfa düğmesiyle tamamlanır).
  const onFinding = (f) => {
    const af = f.actionFilter || {};
    const patch = {};
    if (af.view) patch[Q.view] = af.view === 'gaps' ? null : af.view;
    if (af['competencyId[in]']) patch[Q.competencies] = listOf(af['competencyId[in]']);
    if (af['gapBand[in]']) patch[Q.bands] = listOf(af['gapBand[in]']);
    if (af.hasPlan) patch[Q.plan] = af.hasPlan;
    if (af.persistent) { patch[Q.persistent] = af.persistent; patch[Q.view] = null; }
    setScreen(patch);
    scrollToList();
  };

  const screenActive = SCREEN_KEYS.filter((k) => k !== Q.view && k !== Q.dimension && values[k]);
  const chipText = (k) => {
    const list = listOf(values[k]);
    switch (k) {
      case Q.competencies: return `${t(`${NS}.filter.competency`)} (${list.length})`;
      case Q.groups: return `${t(`${NS}.filter.group`)}: ${list.join(', ')}`;
      case Q.types: return `${t(`${NS}.filter.type`)}: ${list.map((x) => t(`${NS}.competencyType.${x}`)).join(', ')}`;
      case Q.levels: return `${t(`${NS}.filter.expectedLevel`)}: ${list.join(', ')}`;
      case Q.bands: return `${t(`${NS}.filter.gapBand`)}: ${list.map((x) => t(`${NS}.gapBand.${x}`)).join(', ')}`;
      case Q.plan: return `${t(`${NS}.filter.plan`)}: ${t(`${NS}.plan.${values[k] === 'true' ? 'has' : 'hasNot'}`)}`;
      case Q.persistent: return t(`${NS}.filter.persistent`);
      default: return k;
    }
  };

  const m = (code) => metricByCode(summary.data?.metrics, code);
  const common = { ns: NS, loading: summary.loading, error: summary.error, onRetry: summary.retry };
  const missingSub = summary.data ? t(`${NS}.kpi.missingSub`, {
    noExpected: formatNum(summary.data.recordsWithoutExpected, lang, 0),
    notScored: formatNum(summary.data.requiredNotScored, lang, 0),
    blocked: formatNum(summary.data.blockedPersons, lang, 0),
  }) : '';

  return (
    <PageWrap>
      <Breadcrumbs items={[{ label: t(`${NS}.title`) }]} />
      {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.operations.noPeriods')}</PageEmpty> : null}
      {periods.length > 0 ? (
        <PmAnalyticsFilterBar
          periods={periods}
          filters={filters}
          onChange={setFilters}
          onReset={reset}
          lastUpdated={oldestCalculatedAt(metas)}
          stale={anyStale(metas)}
          groupMembership={filters.groupIds.length ? 'current' : null}
        />
      ) : null}
      {ready ? (
        <>
          <ScreenFilters data-filters="screen">
            <ScreenField $w="260px">
              <AsyncGenericSelect
                label={t(`${NS}.filter.competency`)} value={listOf(values[Q.competencies])} isMulti minChars={0} compact
                onChange={(v) => setScreen({ [Q.competencies]: v || [] })} {...competencyLookup}
              />
            </ScreenField>
            <ScreenField>
              <AsyncGenericSelect
                label={t(`${NS}.filter.group`)} value={listOf(values[Q.groups])} isMulti minChars={0} compact
                onChange={(v) => setScreen({ [Q.groups]: v || [] })} {...groupLookup}
              />
            </ScreenField>
            <ScreenField $w="180px">
              <AsyncGenericSelect
                label={t(`${NS}.filter.type`)} value={listOf(values[Q.types])} isMulti minChars={0} compact
                onChange={(v) => setScreen({ [Q.types]: v || [] })} {...typeLookup}
              />
            </ScreenField>
            <ScreenField $w="180px">
              <AsyncGenericSelect
                label={t(`${NS}.filter.expectedLevel`)} value={listOf(values[Q.levels])} isMulti minChars={0} compact
                onChange={(v) => setScreen({ [Q.levels]: v || [] })} {...levelLookup}
              />
            </ScreenField>
            <ScreenField>
              <AsyncGenericSelect
                label={t(`${NS}.filter.gapBand`)} value={listOf(values[Q.bands])} isMulti minChars={0} compact
                onChange={(v) => setScreen({ [Q.bands]: v || [] })} {...bandLookup}
              />
            </ScreenField>
            {tdVisible ? (
              <ScreenField $w="180px">
                <GenericSelect
                  label={t(`${NS}.filter.plan`)} value={values[Q.plan] || null} options={planOptions} idKey="id" labelKey="name"
                  onChange={(v) => setScreen({ [Q.plan]: v || null })}
                />
              </ScreenField>
            ) : null}
          </ScreenFilters>
          {screenActive.length > 0 ? (
            <Chips>
              {screenActive.map((k) => (
                <Chip key={k} data-chip={k}>
                  {chipText(k)}
                  <Button variant="ghost" aria-label={t('performance.operations.filter.remove')} onClick={() => setScreen({ [k]: null })}>
                    <X size={12} />
                  </Button>
                </Chip>
              ))}
              <Button variant="ghost" data-action="screen-reset" onClick={() => setScreen(Object.fromEntries(screenActive.map((k) => [k, null])))}>
                <RotateCcw size={14} />
                {' '}
                {t(`${NS}.filter.clear`)}
              </Button>
            </Chips>
          ) : null}
          <Grid key={filters.periodId}>
            <EvalKpi {...common} code="M54" title={t(`${NS}.kpi.M54`)} metric={m('M54')} />
            <EvalKpi {...common} code="M15" title={t(`${NS}.kpi.M15`)} metric={m('M15')} />
            <EvalKpi {...common} code="M16" title={t(`${NS}.kpi.M16`)} metric={m('M16')} signed />
            <EvalKpi {...common} code="M17" title={t(`${NS}.kpi.M17`)} metric={m('M17')} sub={missingSub} />
            {context && !context.systemBEnabled ? (
              <PageEmpty data-state="no-360" style={{ gridColumn: 'span 12' }}>{t(`${NS}.no360`)}</PageEmpty>
            ) : null}
            <CompetencyHeatmap
              filters={filters} screen={screen} dimension={dimension} onMeta={onMeta} isHr={isHr}
              onDimension={(d) => setScreen({ [Q.dimension]: d === 'department' ? null : d })}
              onOpen={openCell}
            />
            <TopAreasPanel filters={filters} screen={screen} onOpen={openArea} onMeta={onMeta} />
            <FindingsPanel ns={NS} panel={findings} span={7} onAction={onFinding} />
            <CompetencyRecords
              filters={filters} screen={screen} view={view} summary={summary.data} context={context}
              onView={(v) => setScreen({ [Q.view]: v === 'gaps' ? null : v, ...(v !== 'gaps' ? { [Q.persistent]: null } : {}) })}
              canInvite={isHr && canWrite?.('TD')}
            />
          </Grid>
        </>
      ) : null}
    </PageWrap>
  );
}
